import { Check, CircleDashed, Clock3, StickyNote, UserCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { getTimeOffStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';
import {
  ApprovalStatus,
  TimeOffRequest,
  TimeOffRequestDay,
} from '@/modules/time-off/types/time-off-request';
import { TimeOffStatus } from '@/modules/time-off/types/time-off-status';

import { getTimeOffDayLabel } from '../../helpers/time-off-day-label.helper';
import { TimeOffStatusSwatch } from './time-off-status-swatch';

interface TimeOffTooltipContentProps {
  request: TimeOffRequest;
  /** The specific day this tooltip describes (drives the part-of-day / hourly label). */
  day?: TimeOffRequestDay;
}

/**
 * Tooltip body for a single time-off segment in the week-view grid. The first
 * row is the type; below it a small set of icon rows surface the status (as the
 * shared `StatusBadge`), the day's part-of-day / hourly amount, the approvers
 * (an indented list with avatars + decision icons) and the description.
 */
export function TimeOffTooltipContent({ request, day }: TimeOffTooltipContentProps) {
  const { t } = useTranslation(['authenticated.timeOff', 'authenticated.timeTracking']);
  const isApproved = request.status === TimeOffStatus.Approved;
  const approvedCount = request.approvers.filter(a => a.status === ApprovalStatus.Approved).length;

  return (
    <div className="flex max-w-72 flex-col gap-2 text-left">
      {/* Type */}
      <div className="flex items-center gap-1.5">
        <TimeOffStatusSwatch isApproved={isApproved} className="h-2.5 w-2.5" />
        <span className="text-sm font-semibold">{request.type.displayName}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Row icon={UserCheck}>
          <StatusBadge
            type={StatusBadgeType.TimeOff}
            status={request.status}
            label={getTimeOffStatusText(request.status)}
          />
        </Row>

        {day && <Row icon={Clock3}>{getTimeOffDayLabel(day, t)}</Row>}

        {/* Approvers: count summary + an indented list (avatar + name + decision icon). */}
        {request.approvers.length > 0 && (
          <Row icon={UserCheck}>
            <div className="flex flex-col gap-1">
              <span>
                {t('authenticated.timeTracking:timeOff.approvedCount', {
                  approved: approvedCount,
                  total: request.approvers.length,
                })}
              </span>
              <ul className="ml-1 flex flex-col gap-1.5 border-l border-muted-foreground/40 pl-2">
                {request.approvers.map(approver => (
                  <li key={approver.user.id} className="flex items-center gap-1.5">
                    <EntityAvatar
                      image={approver.user.profileImageUrl}
                      name={approver.user.name}
                      size="xs"
                      shape="circle"
                    />
                    <span className="min-w-0 flex-1 truncate text-[12.5px]">
                      {approver.user.name}
                    </span>
                    <ApproverStatusIcon status={approver.status} />
                  </li>
                ))}
              </ul>
            </div>
          </Row>
        )}

        {request.description && (
          <Row icon={StickyNote}>
            <span className="line-clamp-2">{request.description}</span>
          </Row>
        )}
      </div>
    </div>
  );
}

function ApproverStatusIcon({ status }: { status: ApprovalStatus }) {
  if (status === ApprovalStatus.Approved) {
    return <Check size={14} className="shrink-0 text-emerald-500" />;
  }
  return (
    <CircleDashed
      size={14}
      className={cn(
        'shrink-0',
        status === ApprovalStatus.Rejected ? 'text-destructive' : 'text-muted-foreground'
      )}
    />
  );
}

function Row({ icon: Icon, children }: { icon: typeof Clock3; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[13px] text-foreground">
      <Icon size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}
